import { DataLayerWrapper } from "../../../../../components/DataLayerWrapper/DataLayerWrapper";
import * as styles from "./SecondaryNavigation.module.scss";

import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Button } from "../../../../../components";
import PromotionCardToDisplay from "../../../../../components/SignPostCards/PromotionCards/PromotionCardToDisplay";
import { Chevron } from "../../../../../components/common/Icons";
import LinkNoStyle from "../../../../../components/common/LinkNoStyle/LinkNoStyle";
import { isSpecificSite } from "../../../../../utils/isSpecificSite";

export interface MenuOption {
	title: string;
	id: string;
}
export interface HeaderProps {
	isVisible: boolean;
	nestedLinks: DogsTrust.MenuItem[];
	onHide: () => void;
	promoCards: DogsTrust.PromotionCardToDisplayProps[];
	primaryLink: DogsTrust.MenuItem;
}

export const SecondaryNavigation: React.FC<HeaderProps> = ({
	isVisible,
	nestedLinks,
	onHide,
	promoCards,
	primaryLink,
}) => {
	isSpecificSite("ie", "SecondaryNavigation");
	const [thirdMenuToShow, setThirdMenuToShow] = useState<string>(null);
	useEffect(() => {
		if (!isVisible) {
			setThirdMenuToShow(null);
		}
	}, [isVisible]);

	const hasNoTertiaryLinks =
		nestedLinks.find((secondaryLink) => secondaryLink.title === thirdMenuToShow)
			?.thirdLevelItems?.length === 0;

	const onSelectSecondaryLink = useCallback(
		(secondaryLinkTitle: string) => {
			if (thirdMenuToShow === secondaryLinkTitle) {
				setThirdMenuToShow(null);
			} else {
				setThirdMenuToShow(secondaryLinkTitle);

				// Ensure the link for the secondary title is focused when tertiary menu is loaded
				document.getElementById(secondaryLinkTitle)?.focus();

				// Ensure it is scrolled to the top as it sometimes isn't
				document
					.getElementById(`tertiarylist-${secondaryLinkTitle}`)
					?.scrollTo({
						top: 0,
						behavior: "instant",
					});
			}
		},
		[thirdMenuToShow],
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				return onHide();
			}
		},
		[onHide],
	);
	const onTertiaryKeyDown = useCallback(
		(e: React.KeyboardEvent, secondaryLinkTitle: string) => {
			if (e.key === "ArrowLeft") {
				const parentLinkButton = document.getElementById(
					`tertiaryTriggerCta-${secondaryLinkTitle}`,
				);
				parentLinkButton?.focus();
				return setThirdMenuToShow(null);
			}
			if (e.key === "Escape") {
				return onHide();
			}
		},
		[onHide],
	);
	return (
		<div
			className={
				!isVisible
					? styles.secondarynavwrapperhidden
					: styles.secondarynavwrapper
			}
			style={{
				width: "100%",
				top: 56,
			}}
		>
			<div className={styles.secondarynavinner}>
				<ul className={styles.secondarynavlist}>
					<li className={styles.primarylinkwrapper}>
						<LinkNoStyle
							to={primaryLink.url}
							tabIndex={isVisible ? 0 : -1}
							aria-hidden={!isVisible}
							onKeyDown={onKeyDown}
						>
							<div className={styles.primarylink}>{primaryLink.title}</div>
						</LinkNoStyle>
					</li>
					{nestedLinks.map((secondaryLink, i) => (
						<Fragment key={`${secondaryLink.id}-${i}`}>
							<li
								className={
									secondaryLink.title === thirdMenuToShow
										? styles.secondarylinkactive
										: styles.secondarylink
								}
								aria-haspopup={
									secondaryLink.thirdLevelItems?.length > 0 ? "true" : "false"
								}
								aria-expanded={secondaryLink.title === thirdMenuToShow}
							>
								<DataLayerWrapper
									event={"menu_navigation"}
									menu_level={"sub"}
									menu_clicked={secondaryLink.title}
									menu_url={secondaryLink.url}
								>
									{secondaryLink.thirdLevelItems?.length === 0 ? (
										<LinkNoStyle
											to={secondaryLink.url}
											tabIndex={isVisible ? 0 : -1}
											aria-hidden={!isVisible}
											onKeyDown={onKeyDown}
										>
											{secondaryLink.title}
										</LinkNoStyle>
									) : (
										<Button
											id={`tertiaryTriggerCta-${secondaryLink.title}`}
											className={styles.tertiaryTriggerCta}
											variant="tertiary"
											onClick={() => onSelectSecondaryLink(secondaryLink.title)}
											tabIndex={isVisible ? 0 : -1}
											onKeyDown={onKeyDown}
											ariaExpanded={secondaryLink.title === thirdMenuToShow}
											ariaLabel={`${thirdMenuToShow ? "Close" : "Open"} ${secondaryLink.title} sub menu`}
											ariaHasPopup={"menu"}
										>
											{secondaryLink.title}
											{<Chevron rotation={90} height={"20px"} />}
										</Button>
									)}
								</DataLayerWrapper>
							</li>
							<div
								className={
									secondaryLink.title === thirdMenuToShow
										? styles.tertiarylistwrapper
										: styles.tertiarylisthidden
								}
							>
								{secondaryLink.thirdLevelItems.length > 0 && (
									<ul
											className={styles.tertiarylist}
											tabIndex={-1}
											aria-hidden={thirdMenuToShow === null}
											id={`tertiarylist-${secondaryLink.title}`}
										>
											<li className={styles.secondarylinkwrapper}>
												<LinkNoStyle
													id={secondaryLink.title}
													to={secondaryLink.url}
													tabIndex={
														thirdMenuToShow === secondaryLink.title ? 0 : -1
													}
													aria-hidden={thirdMenuToShow !== secondaryLink.title}
													className={styles.secondaryTitle}
													aria-label={`Visit ${secondaryLink.title}`}
													onKeyDown={(e) =>
														onTertiaryKeyDown(e, secondaryLink.title)
													}
												>
													{secondaryLink.title}
												</LinkNoStyle>
											</li>
											{secondaryLink.thirdLevelItems.map((tertiaryLink, i) => (
												<li
													key={`tertiaryLink-${tertiaryLink.id}-${i}`}
													className={styles.tertiarylinkwrapper}
													onKeyDown={(e) =>
														onTertiaryKeyDown(e, secondaryLink.title)
													}
												>
													<DataLayerWrapper
														event={"menu_navigation"}
														menu_level={"sub"}
														menu_clicked={tertiaryLink.title}
														menu_url={tertiaryLink.url}
													>
														<LinkNoStyle
															tabIndex={
																thirdMenuToShow === secondaryLink.title ? 0 : -1
															}
															aria-hidden={
																thirdMenuToShow !== secondaryLink.title
															}
															to={tertiaryLink.url}
														>
															{tertiaryLink.title}
														</LinkNoStyle>
													</DataLayerWrapper>
												</li>
											))}
										</ul>
								)}
							</div>
						</Fragment>
					))}
				</ul>
				{((!thirdMenuToShow && isVisible) ||
					(thirdMenuToShow && hasNoTertiaryLinks)) && (
					<div className={styles.promocardsnotertiary}>
						{promoCards.map((promoCard) => (
							<PromotionCardToDisplay key={promoCard.title} {...promoCard} />
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default SecondaryNavigation;
